import React, { useState, useRef, useEffect } from "react";
import { http, API, formatTRY, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, FileSpreadsheet, FileText, Loader2, User, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { generateAnalysisPdf } from "@/lib/pdfExport";

const SAMPLE_QUESTIONS = [
  "Haziran ayında Borusan ile kaç taşıma yaptım, maliyeti ne oldu?",
  "Gebze Depo'dan çıkan en pahalı 3 sevkiyat nedir?",
  "Hangi nakliyeci son 30 günde en düşük maliyet/ton sundu?",
  "Karayolu ve denizyolu maliyetlerini karşılaştır.",
  "İzmir Liman lokasyonunun bu yılki toplam maliyeti ne oldu?",
];

function MarkdownLike({ text }) {
  if (!text) return null;
  const lines = text.split("\n").filter((l) => l.trim());
  return (
    <div className="space-y-2 text-sm leading-relaxed text-slate-800">
      {lines.map((line, i) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={j} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
          ) : (
            <React.Fragment key={j}>{p}</React.Fragment>
          )
        );
        return <p key={i}>{parts}</p>;
      })}
    </div>
  );
}

function FilterChips({ filter }) {
  if (!filter) return null;
  const items = [];
  if (filter.start_date && filter.end_date) items.push(`${filter.start_date} → ${filter.end_date}`);
  if (filter.carrier_name) items.push(`🚛 ${filter.carrier_name}`);
  if (filter.source_name) items.push(`📍 ${filter.source_name}`);
  if (filter.mode) items.push(`🛣 ${filter.mode}`);
  if (!items.length) return <span className="text-[11px] text-slate-400">Filtre uygulanmadı</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className="text-[11px] bg-white border border-slate-200 text-slate-700 rounded px-2 py-0.5 font-medium">
          {it}
        </span>
      ))}
    </div>
  );
}

function SummaryStrip({ summary }) {
  if (!summary || summary.total_shipments === 0) return null;
  return (
    <div className="grid grid-cols-4 gap-2 mt-3">
      <Mini label="Sevkiyat" value={formatNumber(summary.total_shipments)} />
      <Mini label="Toplam" value={formatTRY(summary.total_cost)} />
      <Mini label="Ort. Maliyet" value={formatTRY(summary.avg_cost)} />
      <Mini label="Zamanında" value={`%${formatNumber(summary.on_time_pct)}`} />
    </div>
  );
}
const Mini = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
    <div className="text-[10px] uppercase tracking-[0.08em] text-slate-500 font-semibold">{label}</div>
    <div className="font-display text-sm font-bold text-slate-900 mt-0.5 tabular-nums">{value}</div>
  </div>
);

export default function AiAssistant() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question) return;
    setInput("");
    const userMsg = { role: "user", content: question, id: `u-${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await http.post("/dashboard/chat", { question });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          id: `a-${Date.now()}`,
          content: res.data.answer,
          filter: res.data.filter_applied,
          summary: res.data.summary,
          rowCount: res.data.row_count,
          queryToken: res.data.query_token,
        },
      ]);
    } catch (err) {
      toast.error("AI cevabı alınamadı");
      setMessages((m) => [
        ...m,
        { role: "assistant", id: `a-${Date.now()}`, content: "Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.", error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async (msg) => {
    if (!msg.queryToken) return;
    try {
      const res = await http.get(`/dashboard/chat/${msg.queryToken}/rows`, { params: { limit: 1000 } });
      const { rows, summary, filter, question, answer } = res.data;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Özet
      const overview = [
        ["SAP TM AI Asistan – Analiz Raporu"],
        [],
        ["Soru", question],
        ["Oluşturulma", new Date().toLocaleString("tr-TR")],
        [],
        ["Uygulanan Filtre"],
        ["Başlangıç", filter?.start_date || "-"],
        ["Bitiş", filter?.end_date || "-"],
        ["Nakliyeci", filter?.carrier_name || "Tümü"],
        ["Kaynak Lokasyon", filter?.source_name || "Tümü"],
        ["Taşıma Modu", filter?.mode || "Tümü"],
        [],
        ["Özet Metrikler"],
        ["Toplam Sevkiyat", summary.total_shipments],
        ["Toplam Maliyet (₺)", summary.total_cost],
        ["Ortalama Maliyet (₺)", summary.avg_cost],
        ["Toplam Ağırlık (ton)", summary.total_tons],
        ["Maliyet / Ton (₺)", summary.cost_per_ton],
        ["Zamanında Teslimat (%)", summary.on_time_pct],
        [],
        ["AI Cevabı"],
        [answer],
      ];
      const wsOverview = XLSX.utils.aoa_to_sheet(overview);
      wsOverview["!cols"] = [{ wch: 28 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, wsOverview, "Özet");

      // Sheet 2: Detaylı sevkiyatlar
      const shipmentsData = rows.map((r) => ({
        "TM ID": r.id,
        "Tarih": r.date.slice(0, 10),
        "Kaynak Lokasyon": r.source_name,
        "Nakliyeci": r.carrier_name,
        "Mod": r.mode,
        "Ağırlık (ton)": r.weight_tons,
        "Maliyet (₺)": r.cost_try,
        "Zamanında": r.on_time ? "Evet" : "Hayır",
        "Durum": r.status,
      }));
      const wsShip = XLSX.utils.json_to_sheet(shipmentsData);
      wsShip["!cols"] = [
        { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 20 }, { wch: 12 },
        { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, wsShip, "Sevkiyatlar");

      // Sheet 3: Nakliyeci kırılımı
      const carrierBreakdown = Object.entries(summary.by_carrier || {}).map(([name, v]) => ({
        "Nakliyeci": name,
        "Sevkiyat": v.count,
        "Toplam Maliyet (₺)": v.cost,
        "Pay (%)": Math.round((v.count / summary.total_shipments) * 1000) / 10,
      }));
      if (carrierBreakdown.length) {
        const ws = XLSX.utils.json_to_sheet(carrierBreakdown);
        ws["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 18 }, { wch: 10 }];
        XLSX.utils.book_append_sheet(wb, ws, "Nakliyeci");
      }

      // Sheet 4: Lokasyon kırılımı
      const locBreakdown = Object.entries(summary.by_location || {}).map(([name, v]) => ({
        "Kaynak Lokasyon": name,
        "Sevkiyat": v.count,
        "Toplam Maliyet (₺)": v.cost,
      }));
      if (locBreakdown.length) {
        const ws = XLSX.utils.json_to_sheet(locBreakdown);
        ws["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws, "Lokasyon");
      }

      // Sheet 5: Mod kırılımı
      const modeBreakdown = Object.entries(summary.by_mode || {}).map(([name, v]) => ({
        "Taşıma Modu": name,
        "Sevkiyat": v.count,
        "Toplam Maliyet (₺)": v.cost,
      }));
      if (modeBreakdown.length) {
        const ws = XLSX.utils.json_to_sheet(modeBreakdown);
        ws["!cols"] = [{ wch: 16 }, { wch: 12 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws, "Mod");
      }

      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `sap-tm-analiz-${stamp}.xlsx`);
      toast.success("Excel analizi indirildi");
    } catch (err) {
      toast.error("Excel oluşturulamadı");
    }
  };

  const exportPdf = async (msg) => {
    if (!msg.queryToken) return;
    try {
      const res = await http.get(`/dashboard/chat/${msg.queryToken}/rows`, { params: { limit: 500 } });
      generateAnalysisPdf(res.data);
      toast.success("PDF raporu indirildi");
    } catch (err) {
      toast.error("PDF oluşturulamadı");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* Chat area */}
      <div className="bg-white border border-slate-200 rounded-lg flex flex-col h-[calc(100vh-220px)] min-h-[500px]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#E5F0FA] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#0070F2]" />
          </div>
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">SAP TM AI Asistan</h2>
            <p className="text-xs text-slate-500">Doğal dilde sor, anında cevap + Excel analizi al</p>
          </div>
        </div>

        <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="text-center text-slate-400 py-12">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Sağdaki örnek sorulardan birini seç veya kendi sorunu yaz.</p>
            </div>
          )}

          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} data-testid="chat-msg-user" className="flex justify-end fade-up">
                <div className="bg-[#0070F2] text-white rounded-lg rounded-tr-sm px-4 py-2.5 max-w-[80%]">
                  <div className="text-sm">{m.content}</div>
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center ml-2 flex-shrink-0">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
              </div>
            ) : (
              <div key={m.id} data-testid="chat-msg-assistant" className="flex justify-start fade-up">
                <div className="w-8 h-8 rounded-full bg-[#E5F0FA] flex items-center justify-center mr-2 flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-[#0070F2]" />
                </div>
                <div className="max-w-[85%] flex-1">
                  <div className={`rounded-lg rounded-tl-sm px-4 py-3 ${m.error ? "bg-rose-50 border border-rose-200" : "bg-[#F0F7FF] border border-[#BAE6FD]"}`}>
                    <MarkdownLike text={m.content} />
                    {m.filter && (
                      <div className="mt-3 pt-3 border-t border-[#BAE6FD]/60">
                        <FilterChips filter={m.filter} />
                      </div>
                    )}
                    <SummaryStrip summary={m.summary} />
                    {m.queryToken && m.rowCount > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                          data-testid={`export-excel-${m.id}`}
                          onClick={() => exportExcel(m)}
                          size="sm"
                          className="bg-[#0070F2] hover:bg-[#0054E3] text-white h-8"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
                        </Button>
                        <Button
                          data-testid={`export-pdf-${m.id}`}
                          onClick={() => exportPdf(m)}
                          size="sm"
                          variant="outline"
                          className="h-8 border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF Raporu
                        </Button>
                        <span className="text-[11px] text-slate-500">{m.rowCount} kayıt</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          )}

          {loading && (
            <div className="flex justify-start fade-up" data-testid="chat-loading">
              <div className="w-8 h-8 rounded-full bg-[#E5F0FA] flex items-center justify-center mr-2 flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[#0070F2]" />
              </div>
              <div className="bg-[#F0F7FF] border border-[#BAE6FD] rounded-lg rounded-tl-sm px-4 py-3 flex items-center gap-2 text-[#0054E3] text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Veriler analiz ediliyor…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-200 p-3 flex gap-2 items-end">
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Örn: Mart ayında Ekol Lojistik ile yaptığım taşımaların maliyetini ver"
            className="flex-1 min-h-[44px] max-h-[120px] resize-none bg-white"
            rows={1}
          />
          <Button
            data-testid="chat-send"
            onClick={() => ask()}
            disabled={loading || !input.trim()}
            className="bg-[#0070F2] hover:bg-[#0054E3] text-white h-[44px]"
          >
            <Send className="w-4 h-4 mr-1.5" /> Sor
          </Button>
        </div>
      </div>

      {/* Sidebar: sample questions */}
      <aside className="bg-white border border-slate-200 rounded-lg p-5 h-fit">
        <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold mb-3">
          Örnek Sorular
        </div>
        <div className="space-y-2">
          {SAMPLE_QUESTIONS.map((q, i) => (
            <button
              key={i}
              data-testid={`sample-question-${i}`}
              onClick={() => ask(q)}
              disabled={loading}
              className="w-full text-left text-sm text-slate-700 hover:text-[#0054E3] hover:bg-[#F0F7FF] border border-slate-200 hover:border-[#BAE6FD] rounded-md px-3 py-2.5 transition-colors disabled:opacity-50"
            >
              {q}
            </button>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-slate-200">
          <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold mb-2">
            İpucu
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Tarih (Haziran, son 30 gün), nakliyeci adı, lokasyon, taşıma modu kullan. Cevabın altındaki
            <strong className="text-slate-900"> Excel</strong> veya <strong className="text-slate-900">PDF</strong> butonu detaylı analiz raporu üretir.
          </p>
        </div>
      </aside>
    </div>
  );
}
